import React, { useState, useEffect, useMemo } from 'react';

import styled from 'styled-components';
import palette from '../../lib/styles/palette';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import SensorIssueGuide from '../runningtrack/SensorIssueGuide';
import AppIssueGuide from '../runningtrack/AppIssueGuide';
import ZetaRangePicker from './zeta/ZetaRangePicker';
import ZetaFaceFailModal from './zeta/ZetaFaceFailModal';
import ZetaLoginListModal from './zeta/ZetaLoginListModal';
import { FACE_FAIL_REASON, FAIL_LIST_LIMIT, faceFailReasonText, groupFaceFails, rangeLabel, eachDay, dayKey, fromUnix, md, toUnix } from './zeta/zetaUtil';

const Container = styled.div`
  background: #fff;
  border: 1px solid #e0e4ea;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

const MainTitle = styled.h2`
  margin: 0;
  color: #2c3e50;
  font-size: 1.15rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;

  &.realtime::before {
    content: '🔴';
    font-size: 14px;
    animation: blink 2s infinite;
  }

  @keyframes blink {
    0%, 50%, 100% { opacity: 1; }
    25%, 75% { opacity: 0.3; }
  }
`;

const Section = styled.div`
  margin: 0 24px 24px;
  border: 1px solid #e0e4ea;
  border-radius: 12px;
  overflow: hidden;

  &:first-of-type {
    margin-top: 24px;
  }
`;

const SectionTitle = styled.h3`
  margin: 0;
  padding: 16px 20px;
  background: #f5f7fa;
  border-bottom: 1px solid #e0e4ea;
  color: #2c3e50;
  font-size: 1rem;
  font-weight: 700;
`;

const SectionBody = styled.div`
  padding: 20px;
`;

const DeviceList = styled.div`
  padding: 20px 20px 0;
  margin-bottom: 16px;
`;

const DeviceItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  margin-bottom: 8px;
  background-color: #f8f9fb;
  border: 1px solid #e0e4ea;
  border-radius: 6px;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f9fbfd;
  }
`;

const DeviceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const DeviceNumber = styled.span`
  color: #2c3e50;
  font-size: 1rem;
  font-weight: 700;
  min-width: 80px;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DeviceStatus = styled.span`
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
  color: #fff;
  background-color: ${props => {
    if (props.type === 'exercise') return '#2e7d32'; // 운동중 - 초록색
    if (props.type === 'stop') return '#9e9e9e'; // 정지 - 회색
    return '#9e9e9e';
  }};
`;

const AppStatus = styled.span`
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
  border: 1px solid ${props => props.type === 'app-normal' ? palette.webCyan[5] : '#e74c3c'};
  color: ${props => props.type === 'app-normal' ? palette.webCyan[5] : '#e74c3c'};
  background-color: ${props => props.type === 'app-normal' ? 'rgba(26,115,233,0.08)' : '#fff5f5'};
`;

const ExerciseInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const InfoText = styled.span`
  font-size: 13px;
  color: ${props => props.warning ? '#e74c3c' : '#868e96'};
  font-weight: ${props => props.warning ? '600' : '400'};
`;

const MemberInfoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background-color: #fff;
  border-radius: 20px;
  border: 1px solid #e0e4ea;
`;

const MemberName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
`;

const LoginBadge = styled.span`
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 8px;
  background-color: ${props => {
    if (props.type === 'face') return '#e3f2fd';
    if (props.type === 'phone') return '#f3e5f5';
    return palette.webGray[2];
  }};
  color: ${props => {
    if (props.type === 'face') return '#1976d2';
    if (props.type === 'phone') return '#7b1fa2';
    return palette.webGray[7];
  }};
  font-weight: 500;
`;

const TimeBadge = styled.span`
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 8px;
  background-color: #fff3e0;
  color: #f57c00;
  font-weight: 500;
  font-family: 'Roboto Mono', monospace;
`;

const GyroText = styled.span`
  font-size: 10px;
  color: #868e96;
  font-family: 'Roboto Mono', monospace;
  font-weight: 400;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  background: #f8f9fb;
  border-bottom: 1px solid #e0e4ea;
`;

const ModalTitleText = styled.h3`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #2c3e50;
`;

const WeekNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const WeekDisplay = styled.div`
  font-size: 14px;
  color: #555;
  min-width: 200px;
  text-align: center;
`;

const GraphContainer = styled.div`
  padding: 20px;
  min-height: 400px;
`;

const NoDataMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 400px;
  font-size: 1rem;
  color: #868e96;
`;

const SimpleBarChart = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  height: 300px;
  padding: 20px;
  border-bottom: 1px solid #e0e4ea;
  gap: 8px;
`;

const BarItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 80px;
  max-width: 80px;
  height: 200px;
  justify-content: flex-start;
  position: relative;
`;

const Bar = styled.div`
  width: 100%;
  background-color: ${props => props.color || palette.webCyan[5]};
  border-radius: 4px 4px 0 0;
  transition: all 0.3s ease;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 8px;
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  
  &:hover {
    opacity: 0.8;
  }
`;

const BarLabel = styled.div`
  font-size: 12px;
  color: #555;
  margin-top: 8px;
  text-align: center;
`;

const ChartLegend = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 20px;
  padding: 16px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LegendColor = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background-color: ${props => props.color};
`;

const LegendLabel = styled.span`
  font-size: 13px;
  color: #555;
`;

const AddSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 20px 20px;
`;

const UpdateInfo = styled.div`
  margin: 20px 24px 0;
  padding: 10px 14px;
  background-color: #f8f9fb;
  border: 1px solid #e0e4ea;
  border-radius: 6px;
  font-size: 12px;
  color: #868e96;
  text-align: center;
`;

const DashboardContainer = styled.div`
  margin-bottom: 30px;
`;

const DashboardTitle = styled.h3`
  margin: 0 0 20px 0;
  color: #2c3e50;
  font-size: 1.15rem;
  font-weight: 700;
`;

const DashboardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  background: #f8f9fb;
  border-bottom: 1px solid #e0e4ea;
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  padding: 24px;
`;

const DashboardCard = styled.div`
  background-color: #fff;
  border: 1px solid #e0e4ea;
  border-radius: 12px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border-color: ${palette.webCyan[5]};
  }
`;

const CardTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 20px;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const PercentageDisplay = styled.div`
  font-size: 48px;
  font-weight: 700;
  color: ${props => props.color || palette.webCyan[5]};
`;

const CardFooter = styled.div`
  display: flex;
  gap: 16px;
  font-size: 13px;
  color: #868e96;
  margin-top: 12px;
`;

const SplitItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const SplitLabel = styled.div`
  font-size: 13px;
  color: #868e96;
`;

const SplitValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${props => props.color || palette.webCyan[5]};
`;

const TypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  width: 100%;
`;

const TypeItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background-color: #f8f9fb;
  border: 1px solid #e0e4ea;
  border-radius: 8px;
`;

const TypeLabel = styled.div`
  font-size: 12px;
  color: #868e96;
`;

const TypeValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.color || '#2c3e50'};
`;

// ── 얼굴로그인 실패 기록 (2026-10-11) ── 원인 표(FACE_FAIL_REASON)는 zeta/zetaUtil 로 옮겼다.

const FailCountRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 24px;
  width: 100%;
`;

const ReasonChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
`;

const ReasonChip = styled.span`
  padding: 3px 10px;
  border-radius: 12px;
  background-color: #f8f9fb;
  border: 1px solid #e0e4ea;
  font-size: 12px;
  color: #495057;
`;

const FailThumbRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
`;

const FailThumb = styled.img`
  width: 96px;
  height: 72px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e0e4ea;
  background-color: #f1f3f5;
`;

const NoLoginDays = styled.div`
  display: grid;
  gap: 4px;
  width: 100%;
  max-width: 520px;
  font-size: 13px;
  color: #495057;
  .row { display: grid; grid-template-columns: 70px 1fr 48px; gap: 10px; align-items: center; }
  .track { height: 8px; border-radius: 4px; background: #eef0f4; overflow: hidden; }
  .track i { display: block; height: 100%; background: #d32f2f; opacity: 0.7; }
  .n { text-align: right; font-variant-numeric: tabular-nums; }
`;

const AdminClubRunningTrackInfo = ({
    selectClub,
    deviceInfos,
    addDeviceInfo,
    deleteDeviceInfo,
    onRefreshData,
    dashboardData,
    weekGraphData,
    onLoadWeekData,
    onLoadFaceFailList,
    onResetDashboardData,
    dashRange,
    onDashRangeChange,
    socketKey,
}) => {
  const [runningNumber, setRunningNumber] = useState('');
  const [weightNumber, setWeightNumber] = useState('');
  const [tvNumber, setTvNumber] = useState('');
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // 그래프 모달 상태
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('');
  const [weekData, setWeekData] = useState(null);
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0); // 0: 이번주, -1: 지난주, -2: 2주전...

  // 얼굴로그인 실패 기록 모달 — 대시보드가 고른 기간의 기록을 사람별로 합쳐 본다.
  const [failModalOpen, setFailModalOpen] = useState(false);
  // 로그인 유형 그래프의 막대 → 그날 그 유형의 회원 목록
  const [loginList, setLoginList] = useState(null);   // { date, type, count }
  const failGroups = useMemo(
    () => (dashboardData && Array.isArray(dashboardData.faceFailLog) ? groupFaceFails(dashboardData.faceFailLog) : null),
    [dashboardData]
  );

  // deviceInfos를 타입별로 분류
  const runningDevices = deviceInfos.filter(device => 
    device.devicetype === 'running' || device.devicetype === 'kiosk'
  );
  const tvDevices = deviceInfos.filter(device => device.devicetype === 'tv');
  const weightDevices = deviceInfos.filter(device => device.devicetype === 'weight');

  // TV 앱 상태 판단 함수
  const getTVAppStatus = (device) => {
    if (device.active === device.devicetype) {
      return { text: '앱정상', type: 'app-normal' };
    } else {
      return { text: '앱비정상', type: 'app-error' };
    }
  };

  // 러닝머신 운동 상태 판단 함수
  const getExerciseStatus = (device) => {
    if (device.sensorActive === true) {
      return { text: '운동중', type: 'exercise' };
    } else {
      return { text: '정지', type: 'stop' };
    }
  };

  // 러닝머신 앱 상태 판단 함수
  const getRunningAppStatus = (device) => {
    if (device.active === device.devicetype || device.active === 'kiosk') {
      return { text: '앱정상', type: 'app-normal' };
    } else {
      return { text: '앱비정상', type: 'app-error' };
    }
  };

  // 로그인 타입 텍스트 및 타입
  const getLoginTypeInfo = (loginType) => {
    if (loginType === 1) return { text: '얼굴', type: 'face' };
    if (loginType === 2) return { text: '전화', type: 'phone' };
    return { text: '', type: '' };
  };

  // 로그인 후 경과 시간 계산
  const getElapsedTime = (startTime) => {
    if (!startTime || startTime === 0) return '';
    
    const now = Math.floor(Date.now() / 1000);
    const elapsed = now - startTime;
    
    const hours = Math.floor(elapsed / 3600);
    const minutes = Math.floor((elapsed % 3600) / 60);
    const seconds = elapsed % 60;
    
    if (hours > 0) {
      return `${hours}시간 ${minutes}분`;
    } else if (minutes > 0) {
      return `${minutes}분 ${seconds}초`;
    } else {
      return `${seconds}초`;
    }
  };

  // 5초마다 데이터 업데이트
  useEffect(() => {
    if (selectClub && selectClub !== 0) {
      const interval = setInterval(() => {
        if (onRefreshData) {
          onRefreshData();
        }
        setLastUpdate(new Date());
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [selectClub, onRefreshData]);

  // 마지막 업데이트 시간 포맷팅
  const formatLastUpdate = (date) => {
    return date.toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const handleAddDevice = (type) => {
    const number = type === 'tv' ? tvNumber : type === 'weight' ? weightNumber : runningNumber;
    if (number && number.trim() !== '') {
      addDeviceInfo({ devicetype: type, number: number.trim() });
      
      if (type === 'tv') {
        setTvNumber('');
      } else if (type === 'weight') {
        setWeightNumber('');
      } else {
        setRunningNumber('');
      }
    }
  };

  const handleDeleteDevice = (index, type) => {
    const devices = type === 'tv' ? tvDevices : type === 'weight' ? weightDevices : runningDevices;
    const deviceToDelete = devices[index];
    deleteDeviceInfo(deviceToDelete);
  };

  if(selectClub == 0) {
    return null;
  }

  // 대시보드 데이터 계산
  const calculateDashboardMetrics = () => {
    if (!dashboardData) return null;

    const { loginLog } = dashboardData;
    const failLogError = dashboardData.faceFailLog === null;   // 불러오지 못함 ≠ 0건
    const faceFailLog = dashboardData.faceFailLog || [];

    // 얼굴로그인 성공율 — 시도 기준(기존 계산): 성공 ÷ (성공 + 실패 건수)
    const faceLoginSuccess = loginLog && loginLog.faceLoginCount !== undefined
      ? Math.round((loginLog.faceLoginCount / (loginLog.faceLoginCount + loginLog.faceLoginFailCount || 1)) * 100)
      : 0;
    // 방문 기준: 같은 기기 3분 안 연속 실패는 한 번의 실패한 방문으로 센다(실패 기록 합치기 규칙 ①).
    //   실패 기록이 없는 기간(기록 시작 전)에는 줄일 것이 없으니 시도 기준과 같아진다.
    const faceOk = loginLog ? loginLog.faceLoginCount || 0 : 0;
    const merged = failGroups ? faceFailLog.length - failGroups.groups.length : 0;
    const failVisits = Math.max(0, (loginLog ? loginLog.faceLoginFailCount || 0 : 0) - merged);
    const faceLoginVisitSuccess = faceOk + failVisits > 0 ? Math.round((faceOk / (faceOk + failVisits)) * 100) : 0;

    // 얼굴로그인 실패 기록 — 원인별 건수(많은 순)
    const failReasonCounts = {};
    faceFailLog.forEach(f => {
      const key = FACE_FAIL_REASON[f.reason] ? f.reason : 'unknown';
      failReasonCounts[key] = (failReasonCounts[key] || 0) + 1;
    });

    const persons = failGroups ? failGroups.persons : [];
    const metrics = {
      faceLoginSuccess,
      faceLoginVisitSuccess,
      failVisits,
      faceLoginCount: loginLog?.faceLoginCount || 0,
      faceLoginFailCount: loginLog?.faceLoginFailCount || 0,
      numberLoginCount: loginLog?.numberLoginCount || 0,
      notLoginCount: loginLog?.notLoginCount || 0,
      failLogError,
      failLogCount: faceFailLog.length,
      failPersonCount: persons.length,
      failThenNumberCount: persons.filter(p => p.memberID > 0).length,
      failRepeatCount: persons.filter(p => p.rows.length >= 3).length,
      failReasons: Object.entries(failReasonCounts).sort((a, b) => b[1] - a[1]),
      failRecentPhotos: faceFailLog.filter(f => f.imageURL).slice(0, 4),
    };

    return metrics;
  };

  const metrics = calculateDashboardMetrics();

  // 주간 데이터 로드
  const loadWeekDataByOffset = async (type, weekOffset) => {
    if (!onLoadWeekData || !selectClub) return;

    const now = new Date();
    const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1); // 오늘 끝
    endDate.setDate(endDate.getDate() + (weekOffset * 7)); // 주 단위 오프셋 적용
    
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 7); // 7일 전

    const startTime = Math.floor(startDate.getTime() / 1000);
    const endTime = Math.floor(endDate.getTime() / 1000);

    try {
      await onLoadWeekData(selectClub, startTime, endTime);
      
      setWeekData({
        type,
        startDate: startDate,
        endDate: endDate,
        weekOffset: weekOffset
      });
    } catch (error) {
      console.error('주간 데이터 로드 실패:', error);
    }
  };

  // 날짜 포맷팅
  const formatDate = (date) => {
    return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
  };

  // 짧은 날짜 포맷팅 (9/27)
  const formatShortDate = (date) => {
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  // 7일 데이터 배열 생성 (빈 데이터 포함)
  const generateSevenDays = (dataArray, startDate) => {
    const sevenDays = [];
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(currentDate.getDate() + i);
      
      // 해당 날짜의 시작 시간 (0시 0분 0초)
      const dayStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
      const dayStartTimestamp = Math.floor(dayStart.getTime() / 1000);
      
      // 해당 날짜의 종료 시간 (23시 59분 59초)
      const dayEnd = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 23, 59, 59);
      const dayEndTimestamp = Math.floor(dayEnd.getTime() / 1000);
      
      // 해당 날짜의 데이터 찾기 (dateTime 매칭)
      const dayData = dataArray && dataArray.find(d => {
        const dataTime = d.dateTime || d.datetime || d.date;
        return dataTime >= dayStartTimestamp && dataTime <= dayEndTimestamp;
      });
      
      sevenDays.push({
        date: currentDate,
        data: dayData || null
      });
    }
    return sevenDays;
  };

  // 카드 클릭 핸들러
  const handleCardClick = async (type) => {
    setModalType(type);
    setCurrentWeekOffset(0);
    setModalOpen(true);
    await loadWeekDataByOffset(type, 0);
  };

  // 이전/다음 주 핸들러
  const handlePrevWeek = async () => {
    const newOffset = currentWeekOffset - 1;
    setCurrentWeekOffset(newOffset);
    await loadWeekDataByOffset(modalType, newOffset);
  };

  const handleNextWeek = async () => {
    if (currentWeekOffset >= 0) return; // 미래 주는 불가능
    const newOffset = currentWeekOffset + 1;
    setCurrentWeekOffset(newOffset);
    await loadWeekDataByOffset(modalType, newOffset);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setWeekData(null);
    setCurrentWeekOffset(0);
  };

  // 그래프 데이터 렌더링
  const renderGraph = () => {
    if (!weekGraphData) return <NoDataMessage>데이터를 불러오는 중...</NoDataMessage>;

    switch (modalType) {
      case '얼굴로그인':
        return renderFaceLoginGraph();
      case '로그인유형':
        return renderLoginTypeGraph();
      default:
        return <NoDataMessage>데이터가 없습니다.</NoDataMessage>;
    }
  };

  // 얼굴로그인 성공율 그래프
  const renderFaceLoginGraph = () => {
    if (!weekData) return <NoDataMessage>데이터를 불러오는 중...</NoDataMessage>;

    const dataArray = weekGraphData.loginLog || [];
    const sevenDays = generateSevenDays(dataArray, weekData.startDate);
    // 카드와 같은 방문 기준 — 같은 기기 3분 안 연속 실패는 한 번으로 센다(groupFaceFails). 날마다 그날 실패 기록만으로 묶어서
    //   카드를 그 하루로 놓았을 때와 같은 값이 나온다. 실패 기록을 못 받았으면(null) 카드처럼 시도 기준으로 둔다.
    //   ★500건 상한에 걸리면 오래된 날의 기록이 잘렸을 수 있다 → 가장 오래 받은 기록보다 앞에서 시작하는 날은 시도 기준.
    const failLog = Array.isArray(weekGraphData.faceFailLog) ? weekGraphData.faceFailLog : null;
    const capped = !!failLog && failLog.length >= FAIL_LIST_LIMIT;
    const oldestFail = capped ? Math.min(...failLog.map(f => f.dateTime)) : 0;

  return (
      <GraphContainer>
        <SimpleBarChart>
          {sevenDays.map((day, index) => {
            const dayData = day.data;
            const ok = dayData ? dayData.faceLoginCount || 0 : 0;
            let fails = dayData ? dayData.faceLoginFailCount || 0 : 0;
            const dayStart = toUnix(new Date(day.date.getFullYear(), day.date.getMonth(), day.date.getDate()));
            const dayEnd = toUnix(new Date(day.date.getFullYear(), day.date.getMonth(), day.date.getDate() + 1));
            if (failLog && (!capped || dayStart > oldestFail)) {
              const rows = failLog.filter(f => f.dateTime >= dayStart && f.dateTime < dayEnd);
              fails = Math.max(0, fails - (rows.length - groupFaceFails(rows).groups.length));
            }
            const total = ok + fails;
            const successRate = total > 0 ? Math.round((ok / total) * 100) : 0;
            // 100%를 기준으로 높이 계산
            const height = successRate;

            return (
              <BarItem key={index}>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  justifyContent: 'flex-end', 
                  height: '100%',
                  width: '100%'
                }}>
                  <Bar color="#1976d2" style={{ height: height > 0 ? `${height}%` : '2px', minHeight: height > 0 ? 'auto' : '2px', backgroundColor: height > 0 ? '#1976d2' : '#e0e0e0' }}>
                    {height > 0 ? `${successRate}%` : ''}
                  </Bar>
                </div>
                <BarLabel>{formatShortDate(day.date)}</BarLabel>
              </BarItem>
            );
          })}
        </SimpleBarChart>
        <ChartLegend>
          <LegendItem>
            <LegendColor color="#1976d2" />
            <LegendLabel>얼굴로그인 성공율 {failLog ? '(방문 기준 · 카드와 같은 계산)' : '(시도 기준 · 실패 기록을 불러오지 못함)'}</LegendLabel>
          </LegendItem>
        </ChartLegend>
      </GraphContainer>
    );
  };

  // 로그인 유형 그래프
  const renderLoginTypeGraph = () => {
    if (!weekData) return <NoDataMessage>데이터를 불러오는 중...</NoDataMessage>;

    const dataArray = weekGraphData.loginLog || [];
    const sevenDays = generateSevenDays(dataArray, weekData.startDate);
    
    // 전체 최대값 계산 (모든 날짜의 총합 중 최대값)
    const dailyTotals = sevenDays.map(d => {
      if (!d.data) return 0;
      return (d.data.faceLoginCount || 0) + (d.data.numberLoginCount || 0) + (d.data.notLoginCount || 0);
    });
    const maxValue = Math.max(1, ...dailyTotals);
    
    // 디버깅 로그
    console.log('로그인유형 그래프 - 일별 총합:', dailyTotals);
    console.log('로그인유형 그래프 - 최대값:', maxValue);

    return (
      <GraphContainer>
        <SimpleBarChart>
          {sevenDays.map((day, index) => {
            const dayData = day.data;
            const values = [
              { count: dayData?.numberLoginCount || 0, color: '#7b1fa2', label: '번호', type: 'number' },
              { count: dayData?.faceLoginCount || 0, color: '#1976d2', label: '얼굴', type: 'face' },
              { count: dayData?.notLoginCount || 0, color: '#d32f2f', label: '비로그인', type: 'notLogin' },
            ];
            const totalCount = values.reduce((sum, v) => sum + v.count, 0);
            // 전체 높이는 해당 날짜의 총합에 비례
            const totalHeight = totalCount > 0 ? (totalCount / maxValue) * 100 : 0;
            
            // 디버깅 로그
            console.log(`${formatShortDate(day.date)}: totalCount=${totalCount}, maxValue=${maxValue}, totalHeight=${totalHeight}%`);
            console.log(`  - faceLoginCount: ${dayData?.faceLoginCount || 0}`);
            console.log(`  - numberLoginCount: ${dayData?.numberLoginCount || 0}`);
            console.log(`  - notLoginCount: ${dayData?.notLoginCount || 0}`);

            return (
              <BarItem key={index}>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column-reverse', 
                  width: '100%', 
                  height: totalHeight > 0 ? `${totalHeight}%` : '2px', 
                  gap: '1px', 
                  backgroundColor: totalHeight > 0 ? 'transparent' : '#e0e0e0', 
                  borderRadius: '4px 4px 0 0',
                  marginTop: 'auto'
                }}>
                  {totalHeight > 0 && values.map((v, i) => {
                    // 각 세그먼트의 높이는 해당 값에 비례
                    const segmentHeight = totalCount > 0 ? (v.count / totalCount) * 100 : 0;
                    return v.count > 0 ? (
                      <div 
                        key={i}
                        title={`${formatShortDate(day.date)} ${v.label} ${v.count} · 눌러서 회원 보기`}
                        onClick={() => setLoginList({ date: day.date, type: v.type, count: v.count })}
                        style={{ 
                          cursor: 'pointer',
                          height: `${segmentHeight}%`,
                          backgroundColor: v.color,
                          borderRadius: (() => {
                            const validValues = values.filter(val => val.count > 0);
                            const validIndex = validValues.indexOf(v);
                            const isTop = validIndex === validValues.length - 1; // 맨 위 (column-reverse에서 마지막)
                            
                            if (isTop) {
                              return '4px 4px 0 0'; // 맨 위는 상단만 둥글게
                            } else {
                              return '0'; // 나머지는 모두 각져있게
                            }
                          })(),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontSize: '11px',
                          fontWeight: '600',
                          minHeight: segmentHeight > 0 ? '15px' : '0',
                          flex: 'none'
                        }}
                      >
                        {v.count}
                      </div>
                    ) : null;
                  })}
                </div>
                <BarLabel>{formatShortDate(day.date)}</BarLabel>
              </BarItem>
            );
          })}
        </SimpleBarChart>
        <ChartLegend>
          <LegendItem>
            <LegendColor color="#1976d2" />
            <LegendLabel>얼굴</LegendLabel>
          </LegendItem>
          <LegendItem>
            <LegendColor color="#7b1fa2" />
            <LegendLabel>번호</LegendLabel>
          </LegendItem>
          <LegendItem>
            <LegendColor color="#d32f2f" />
            <LegendLabel>비로그인</LegendLabel>
          </LegendItem>
        </ChartLegend>
      </GraphContainer>
    );
  };

  // ── 얼굴로그인 실패 기록 모달 — 기간은 대시보드 기간 선택을 따른다 ──
  const handleFailCardClick = () => setFailModalOpen(true);

  // 비로그인 운동 — 날짜별 횟수(clubloginlog.notLoginCount)
  const noLoginDays = (dashRange && dashboardData && Array.isArray(dashboardData.loginLogDays))
    ? eachDay(dashRange).map((d) => {
        const row = dashboardData.loginLogDays.find((x) => dayKey(fromUnix(x.dateTime)) === dayKey(d));
        return { d, n: row ? row.notLoginCount || 0 : 0 };
      })
    : [];
  const noLoginMax = Math.max(1, ...noLoginDays.map((x) => x.n));

  return (
    <>
        <div style={{ height: '30px' }} />
      {/* 대시보드 — 기간은 다른 탭과 같은 방식으로 고른다 */}
      {metrics && (
        <Container style={{ marginBottom: '30px' }}>
          <DashboardHeader style={{ flexWrap: 'wrap', gap: '12px' }}>
            <MainTitle>📊 대시보드</MainTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
              {dashRange && onDashRangeChange ? <ZetaRangePicker range={dashRange} onChange={onDashRangeChange} idPrefix="zeta-dash" /> : null}
              <button
                onClick={() => onResetDashboardData()}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.15s ease',
                  fontSize: '18px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#eef0f4'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                title="새로고침"
              >
                🔄
              </button>
            </div>
          </DashboardHeader>
          <DashboardGrid>
            {/* 1. 얼굴로그인 성공율 — 방문 기준(연속 실패를 한 번으로) + 시도 기준(기존) */}
            <DashboardCard onClick={() => handleCardClick('얼굴로그인')}>
              <CardTitle>얼굴로그인 성공율</CardTitle>
              <CardContent>
                <PercentageDisplay color="#1976d2">{metrics.failLogError ? metrics.faceLoginSuccess : metrics.faceLoginVisitSuccess}%</PercentageDisplay>
                <CardFooter style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '14px',
                  color: '#666'
                }}>
                  {metrics.failLogError ? (
                    <div style={{ fontWeight: '500' }}>{metrics.faceLoginCount + metrics.faceLoginFailCount}번의 로그인 시도 중</div>
                  ) : (
                    <div style={{ fontWeight: '500' }}>방문 기준 · 연속 실패 {metrics.faceLoginFailCount}건을 {metrics.failVisits}번으로 합침</div>
                  )}
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <span style={{ color: '#4caf50' }}>✓ {metrics.faceLoginCount}번 성공</span>
                    <span style={{ color: '#f44336' }}>✗ {metrics.failLogError ? metrics.faceLoginFailCount : metrics.failVisits}번 실패</span>
                  </div>
                  {!metrics.failLogError ? (
                    <div style={{ fontSize: '12px', color: '#868e96' }}>시도 기준(실패 건마다 셈) {metrics.faceLoginSuccess}%</div>
                  ) : null}
                </CardFooter>
              </CardContent>
            </DashboardCard>

            {/* 2. 로그인 유형 */}
            <DashboardCard onClick={() => handleCardClick('로그인유형')}>
              <CardTitle>로그인 유형</CardTitle>
              <CardContent>
                <TypeGrid>
                  <TypeItem>
                    <TypeLabel>얼굴</TypeLabel>
                    <TypeValue color="#1976d2">{metrics.faceLoginCount}</TypeValue>
                  </TypeItem>
                  <TypeItem>
                    <TypeLabel>번호</TypeLabel>
                    <TypeValue color="#7b1fa2">{metrics.numberLoginCount}</TypeValue>
                  </TypeItem>
                  <TypeItem>
                    <TypeLabel>비로그인</TypeLabel>
                    <TypeValue color="#d32f2f">{metrics.notLoginCount}</TypeValue>
                  </TypeItem>
                </TypeGrid>
                <CardFooter>눌러서 주간 그래프 · 막대를 누르면 그날 회원 목록</CardFooter>
              </CardContent>
            </DashboardCard>

            {/* 3. 얼굴로그인 실패 기록 — 같은 사람의 반복 실패는 한 사람으로 */}
            <DashboardCard style={{ gridColumn: '1 / -1' }} onClick={handleFailCardClick}>
              <CardTitle>얼굴로그인 실패 기록</CardTitle>
              <CardContent>
                {metrics.failLogError ? (
                  <NoDataMessage>실패 기록을 불러오지 못했습니다 (관리자 로그인·클럽 권한을 확인해 주세요)</NoDataMessage>
                ) : (
                <FailCountRow>
                  <SplitItem>
                    <SplitLabel>실패</SplitLabel>
                    <SplitValue color="#f44336">{metrics.failLogCount}건</SplitValue>
                  </SplitItem>
                  <SplitItem>
                    <SplitLabel>사람 기준</SplitLabel>
                    <SplitValue color="#2c3e50">{metrics.failPersonCount}명</SplitValue>
                  </SplitItem>
                  <SplitItem>
                    <SplitLabel>실패 후 번호로 로그인</SplitLabel>
                    <SplitValue color="#7b1fa2">{metrics.failThenNumberCount}명</SplitValue>
                  </SplitItem>
                  <SplitItem>
                    <SplitLabel>3번 이상 실패</SplitLabel>
                    <SplitValue color="#f44336">{metrics.failRepeatCount}명</SplitValue>
                  </SplitItem>
                </FailCountRow>
                )}
                {metrics.failReasons.length > 0 && (
                  <ReasonChips>
                    {metrics.failReasons.map(([reason, count]) => (
                      <ReasonChip key={reason}>{faceFailReasonText(reason)} {count}</ReasonChip>
                    ))}
                  </ReasonChips>
                )}
                {metrics.failRecentPhotos.length > 0 && (
                  <FailThumbRow>
                    {metrics.failRecentPhotos.map(f => (
                      <FailThumb key={f.failKey} src={f.imageURL} alt="실패 순간 사진" />
                    ))}
                  </FailThumbRow>
                )}
                <CardFooter>눌러서 사람별로 합친 실패 기록을 봅니다 (시각·기기·원인·사진·번호 로그인 회원)</CardFooter>
              </CardContent>
            </DashboardCard>

            {/* 4. 비로그인 운동 — 날짜별 횟수 */}
            <DashboardCard style={{ gridColumn: '1 / -1', cursor: 'default' }}>
              <CardTitle>비로그인 운동</CardTitle>
              <CardContent>
                <SplitItem>
                  <SplitLabel>로그인하지 않고 운동한 횟수 (벨트가 멈출 때 1번)</SplitLabel>
                  <SplitValue color="#d32f2f">{metrics.notLoginCount}회</SplitValue>
                </SplitItem>
                {noLoginDays.length > 1 && (
                  <NoLoginDays>
                    {noLoginDays.slice().reverse().map((x) => (
                      <div className="row" key={dayKey(x.d)}>
                        <span>{md(x.d)}</span>
                        <span className="track"><i style={{ width: `${Math.round((x.n / noLoginMax) * 100)}%` }} /></span>
                        <span className="n">{x.n}회</span>
                      </div>
                    ))}
                  </NoLoginDays>
                )}
                <CardFooter>비로그인 운동은 회원 정보가 없어 횟수만 있습니다</CardFooter>
              </CardContent>
            </DashboardCard>
          </DashboardGrid>
        </Container>
      )}

      {/* 실시간 데이터 */}
    <Container>
        <DashboardHeader>
          <MainTitle className="realtime">실시간 데이터</MainTitle>
        </DashboardHeader>
      <UpdateInfo>
        마지막 업데이트: {formatLastUpdate(lastUpdate)} (5초마다 자동 갱신)
      </UpdateInfo>
      

      {/* TV 섹션 */}
      <Section>
        <SectionTitle>TV</SectionTitle>
        <DeviceList>
          {tvDevices.map((device, index) => {
            const appStatus = getTVAppStatus(device);
            return (
            <DeviceItem key={`tv-${index}`}>
              <DeviceInfo>
                  <DeviceNumber>TV {device.number}번</DeviceNumber>
              </DeviceInfo>
                <RightSection>
                  <AppStatus type={appStatus.type}>
                    {appStatus.text}
                  </AppStatus>
              <Button
                    variant="text"
                size="small"
                onClick={() => handleDeleteDevice(index, 'tv')}
                    style={{ 
                      minWidth: '32px', 
                      padding: '4px',
                      color: palette.webGray[5]
                    }}
              >
                    <DeleteIcon fontSize="small" />
              </Button>
                </RightSection>
            </DeviceItem>
            );
          })}
        </DeviceList>
        <AddSection>
          <TextField
            size="small"
            label="머신 번호"
            value={tvNumber}
            onChange={(e) => setTvNumber(e.target.value)}
            variant="outlined"
            style={{ width: '200px' }}
            placeholder="번호를 입력하세요"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleAddDevice('tv')}
            disabled={!tvNumber.trim()}
          >
            추가
          </Button>
        </AddSection>
      </Section>
      
      <AppIssueGuide type="tv" devices={tvDevices}/>
      {/* RUNNING & KIOSK 섹션 */}
      <Section>
        <SectionTitle>러닝머신 기기</SectionTitle>
        <DeviceList>
          {runningDevices.map((device, index) => {
            const exerciseStatus = getExerciseStatus(device);
            const appStatus = getRunningAppStatus(device);
            const isExercising = device.sensorActive === true;
            const loginType = device.loginType || 0;
            const hasLoginInfo = device.memberName || device.startTime;
            const isLoggedIn = loginType > 0;
            
            // 로그인 안하고 운동중일 경우에만 "로그인 안함" 표시
            const showNoLogin = !isLoggedIn && isExercising;
            
            return (
            <DeviceItem key={`running-${index}`}>
              <DeviceInfo>
                  <DeviceNumber>러닝 {device.number}번</DeviceNumber>
                  <ExerciseInfo>
                    <DeviceStatus type={exerciseStatus.type}>
                      {exerciseStatus.text}
                </DeviceStatus>
                    
                    {showNoLogin && (
                      <InfoText warning>
                        로그인안함
                      </InfoText>
                    )}
                    
                    {isLoggedIn && hasLoginInfo && (
                      <MemberInfoContainer>
                        {device.memberName && (
                          <MemberName>{device.memberName}</MemberName>
                        )}
                        {loginType > 0 && (
                          <LoginBadge type={getLoginTypeInfo(loginType).type}>
                            {getLoginTypeInfo(loginType).text}
                          </LoginBadge>
                        )}
                        {device.startTime && (
                          <TimeBadge>⏱ {getElapsedTime(device.startTime)}</TimeBadge>
                        )}
                      </MemberInfoContainer>
                    )}
                  </ExerciseInfo>
              </DeviceInfo>
                <RightSection>
                  {device.gyro && (device.gyro.first !== undefined || device.gyro.second !== undefined || device.gyro.third !== undefined) && (
                    <GyroText>
                      ({device.gyro.first || 0}, {device.gyro.second || 0}, {device.gyro.third || 0})
                    </GyroText>
                  )}
                  <AppStatus type={appStatus.type}>
                    {appStatus.text}
                  </AppStatus>
                  {['normal', 'abnormal'].includes(device.sensorStatus) && <AppStatus type={device.sensorStatus === 'normal' ? 'app-normal' : 'app-error'}>
                    {device.sensorStatus === 'normal' ? '센서 정상' : '센서 비정상'}
                  </AppStatus>}
              <Button
                    variant="text"
                size="small"
                onClick={() => handleDeleteDevice(index, 'running')}
                    style={{ 
                      minWidth: '32px', 
                      padding: '4px',
                      color: palette.webGray[5]
                    }}
              >
                    <DeleteIcon fontSize="small" />
              </Button>
                </RightSection>
            </DeviceItem>
            );
          })}
        </DeviceList>
        <AddSection>
          <TextField
            size="small"
            label="머신 번호"
            value={runningNumber}
            onChange={(e) => setRunningNumber(e.target.value)}
            variant="outlined"
            style={{ width: '200px' }}
            placeholder="번호를 입력하세요"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleAddDevice('running')}
            disabled={!runningNumber.trim()}
          >
            추가
          </Button>
        </AddSection>
        <AppIssueGuide type="running" devices={runningDevices}/>
        <SensorIssueGuide type="running" devices={runningDevices}/>
      </Section>
    </Container>

      <Section>
        <SectionTitle>웨이트 기기</SectionTitle>
        <DeviceList>
          {weightDevices.map((device, index) => <DeviceItem key={'weight-' + device.number}>
            <DeviceNumber>웨이트 {device.number}번</DeviceNumber>
            <RightSection>
              <AppStatus type={device.active === 'weight' ? 'app-normal' : 'app-error'}>{device.active === 'weight' ? '앱 정상' : '앱 비정상'}</AppStatus>
              {['normal', 'abnormal'].includes(device.sensorStatus) && <AppStatus type={device.sensorStatus === 'normal' ? 'app-normal' : 'app-error'}>{device.sensorStatus === 'normal' ? '센서 정상' : '센서 비정상'}</AppStatus>}
              <Button aria-label={'웨이트 ' + device.number + '번 삭제'} onClick={() => handleDeleteDevice(index, 'weight')}><DeleteIcon fontSize="small"/></Button>
            </RightSection>
          </DeviceItem>)}
        </DeviceList>
        <AddSection>
          <TextField size="small" label="웨이트 번호" value={weightNumber} onChange={event => setWeightNumber(event.target.value)} variant="outlined"/>
          <Button variant="contained" color="primary" startIcon={<AddIcon/>} onClick={() => handleAddDevice('weight')} disabled={!weightNumber.trim()}>추가</Button>
        </AddSection>
        <AppIssueGuide type="weight" devices={weightDevices}/>
        <SensorIssueGuide type="weight" devices={weightDevices}/>
      </Section>

      {/* 주간 그래프 모달 */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
      >
        <ModalHeader>
          <ModalTitleText>{modalType} - 주간 통계</ModalTitleText>
          <WeekNavigation>
            <IconButton onClick={handlePrevWeek} size="small">
              <ArrowBackIcon />
            </IconButton>
            {weekData && (
              <WeekDisplay>
                {formatDate(weekData.startDate)} ~ {formatDate(weekData.endDate)}
              </WeekDisplay>
            )}
            <IconButton 
              onClick={handleNextWeek} 
              size="small"
              disabled={currentWeekOffset >= 0}
            >
              <ArrowForwardIcon />
            </IconButton>
            <IconButton onClick={handleCloseModal} size="small">
              <CloseIcon />
            </IconButton>
          </WeekNavigation>
        </ModalHeader>
        <DialogContent>
          {renderGraph()}
        </DialogContent>
      </Dialog>

      {/* 얼굴로그인 실패 기록 — 대시보드 기간, 사람별로 합쳐 보기 */}
      <ZetaFaceFailModal
        open={failModalOpen}
        onClose={() => setFailModalOpen(false)}
        list={dashboardData ? dashboardData.faceFailLog : []}
        rangeText={dashRange ? rangeLabel(dashRange) : ''}
        clubID={selectClub}
        socketKey={socketKey}
      />

      {/* 로그인 유형 그래프의 막대 → 그날 그 유형의 회원 */}
      <ZetaLoginListModal
        open={!!loginList}
        date={loginList ? loginList.date : null}
        type={loginList ? loginList.type : ''}
        count={loginList ? loginList.count : 0}
        clubID={selectClub}
        socketKey={socketKey}
        onClose={() => setLoginList(null)}
      />
    </>
  );
};

export default React.memo(AdminClubRunningTrackInfo);
